<template>
  <v-app
    style="width: 100%"
    class="pa-3 pt-0"
  >
    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      absolute
      bottom
      style="top: -4px"
      color="primary"
    />
    <div class="px-3 d-flex">
      <div style="display: flex">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-bind="attrs"
              icon
              class="float-left mt-1"
              small
              v-on="on"
            >
              <v-icon
                color="primary"
                style="font-size: 22px"
                @click="print"
              >
                mdi-cloud-print-outline
              </v-icon>
            </v-btn>
          </template>
          <span>{{ $t('print') }}</span>
        </v-tooltip>
        <v-chip-group
          v-model="printType"
          active-class="deep-purple--text text--accent-4"
          mandatory
        >
          <v-chip
            small
            value="unofficial"
          >
            {{ $t('unofficial') }}
          </v-chip>
          <v-chip
            small
            value="official"
          >
            {{ $t('official') }}
          </v-chip>
        </v-chip-group>
        <v-card-text
          style="display: none"
          class="py-0"
        >
          <v-chip-group
            v-model="printSize"
            active-class="deep-purple--text text--accent-4"
            mandatory
          >
            <v-chip
              small
              value="A4"
            >
              A4
            </v-chip>
            <v-chip
              small
              value="A5"
            >
              A5
            </v-chip>
            <v-chip
              small
              value="A6"
            >
              A6
            </v-chip>
            <v-chip
              small
              value="10.5cm 7.4cm"
            >
              A7
            </v-chip>
            <v-chip
              small
              value="5.2cm 7.4cm"
            >
              A7
            </v-chip>
            <v-chip
              v-if="!width&&!height"
              small
              @click="customSizeDialog = true"
            >
              {{ $t('custom') }}
            </v-chip>
            <v-chip
              v-if="width&&height"
              small
              close
              color="primary"
              outlined
              @click:close="clearCustom"
              @click="customSizeDialog = true"
            >
              {{ width +'cm' }} × {{ height + 'cm' }}
            </v-chip>
          </v-chip-group>
        </v-card-text>
      </div>
      <v-spacer />
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-shortkey="['f6']"
            v-bind="attrs"
            icon
            small
            class="float-left mt-1"
            @shortkey="goTo(type)"
            v-on="on"
            @click="goTo(type)"
          >
            <v-icon
              color="teal"
              style="font-size: 22px"
            >
              mdi-plus-box-outline
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t(type) }}</span>
      </v-tooltip>
      <v-tooltip
        v-if="entrepot"
        bottom
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-shortkey="['f7']"
            v-bind="attrs"
            icon
            :disabled="(entrepot ? false : true)"
            small
            class="float-left mt-1"
            @shortkey="entrepotPrint()"
            v-on="on"
            @click="entrepotPrint()"
          >
            <v-icon
              color="orange"
              style="font-size: 22px"
            >
              mdi-warehouse
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t(entrepot.type + 'Print') }} (f7)</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            icon
            small
            class="mt-1"
            v-on="on"
            @click="edit"
          >
            <v-icon
              color="primary"
              style="font-size: 22px"
            >
              mdi-clipboard-edit
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('edit') }}</span>
      </v-tooltip>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            icon
            class="mt-1"
            small
            v-on="on"
            @click="goTo( type + 'List' )"
          >
            <v-icon
              color="primary"
              style="font-size: 22px"
            >
              mdi-format-list-numbered
            </v-icon>
          </v-btn>
        </template>
        <span>{{ $t('list') }} {{ $t(type) }}</span>
      </v-tooltip>
    </div>
    <div
      id="print"
      class="px-3"
    >
      <v-data-table
        v-if="headers && totalPrice"
        ref="table"
        :headers="headers"
        :items="items"
        :items-per-page="8000000000000"
        hide-default-footer
      >
        <template v-slot:item.count="{ item }">
          <div align="center">
            {{ $numberFormat(parseFloat(item.count)) }}
          </div>
        </template>
        <template v-slot:item.row="{ item }">
          <div align="center">
            {{ parseInt(items.indexOf(item))+ parseFloat(1) }}
          </div>
        </template>
        <template v-slot:item.stampTranslator="{ item }">
          {{ (item.stampTranslator && item.stampTranslator == 'active' ? $t('have') : $t('notHave')) }}
        </template>
        <template v-slot:item.judicialApproval="{ item }">
          {{ (item.judicialApproval && item.judicialApproval == 'active' ? $t('have') : $t('notHave')) }}
        </template>
        <template v-slot:item.foreignApproval="{ item }">
          {{ (item.foreignApproval && item.foreignApproval == 'active' ? $t('have') : $t('notHave')) }}
        </template>
        <template v-slot:item.basePrice="{ item }">
          <div align="center">
            {{ $numberFormat(parseFloat(item.basePrice)) }}
          </div>
        </template>
        <template v-slot:item.totalCost="{ item }">
          <div align="center">
            {{ $numberFormat(parseFloat(item.totalCost)) }}
          </div>
        </template>
        <template
          v-slot:body.append
        >
          <tr :class="{'v-data-table__mobile-table-row':isMobile}">
            <td
              :colspan="`${isMobile?6:2}`"
              :class="{'v-data-table__mobile-row':isMobile}"
            >
              <strong>{{ $t('typeTranslation&approval') }}</strong>
            </td>
            <td
              :colspan="`${isMobile?6:9}`"
              :class="{'v-data-table__mobile-row':isMobile}"
            >
              <div style="display: flex">
                <v-checkbox
                  v-model="totals.unofficial"
                  :label="$t('unofficial')"
                  :readonly="true"
                  style="margin-inline-end: 20px"
                />
                <v-checkbox
                  v-model="totals.stampTranslator"
                  :label="$t('stampTranslator')"
                  :readonly="true"
                  style="margin-inline-end: 20px"
                />
                <v-checkbox
                  v-model="totals.judicialApproval"
                  :label="$t('judicialApproval')"
                  :readonly="true"
                  style="margin-inline-end: 20px"
                />
                <v-checkbox
                  v-model="totals.foreignApproval"
                  :label="$t('foreignApproval')"
                  :readonly="true"
                  style="margin-inline-end: 20px"
                />
                <v-checkbox
                  v-model="totals.embassy"
                  :label="$t('embassy')"
                  :readonly="true"
                  style="margin-inline-end: 20px"
                />
              </div>
            </td>
          </tr>
          <tr>
            <td
              :colspan="`${isMobile?6:4}`"
              :class="{'v-data-table__mobile-row':isMobile}"
              class="pa-0"
            >
              <v-list
                nav
                dense
              >
                <v-list-item>
                  <v-list-item-content>{{ $t('baseTranslate') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.basePriceTotal) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('addDocument') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.addDocumentPriceTotal) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('specialTranslat') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.specialTranslatTotal) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('specialTranslatAdditional') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.specialTranslatAddPriceTotal) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('judicialApprovalPrice') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.judicialApprovalPrice) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('foreignApprovalPrice') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.foreignApprovalPrice) }}
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </td>
            <td
              :colspan="`${isMobile?6:4}`"
              :class="{'v-data-table__mobile-row':isMobile}"
              class="pa-0"
            >
              <v-list
                nav
                dense
              >
                <v-list-item>
                  <v-list-item-content>{{ $t('stampTranslatorPrice') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.stampTranslatorPrice) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('officeServicesPrice') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.officeServicesPrice) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('verificationServicesPrice') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.verificationServicesPrice) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('equalOriginalPrice') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.equalOriginalPrice) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('inquiryPrice') }}:</v-list-item-content>
                  <v-list-item-content class="align-end">
                    {{ $numberFormat(totalPrice.inquiryPrice || 0) }}
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('embassyPrice') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.embassyPrice) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('urgentAmount') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.urgentAmount) }}
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </td>
            <td
              :colspan="`${isMobile?6:3}`"
              :class="{'v-data-table__mobile-row':isMobile}"
              class="pa-0"
            >
              <v-list
                nav
                dense
              >
                <v-list-item style="background-color: #ffb2ac">
                  <v-list-item-content>{{ $t('discountTranslate') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(+totalPrice.discountTranslate + +totalPrice.discountService) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('payable') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(totalPrice.totalCost) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>{{ $t('payed') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat((totalPayed ? totalPayed.amount : 0)) }}
                  </v-list-item-action>
                </v-list-item>
                <v-list-item style="background-color: #abe09b">
                  <v-list-item-content>{{ $t('remain') }}:</v-list-item-content>
                  <v-list-item-action>
                    {{ $numberFormat(+totalPrice.totalCost - +(totalPayed ? totalPayed.amount : 0)) }}
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </td>
          </tr>
          <tr>
            <td
              :colspan="`${isMobile?6:11}`"
              :class="{'v-data-table__mobile-row':isMobile}"
              style="padding: 10px"
            >
              {{ $t('content') }} : {{ content }}
              <div v-html="self.invoiceContent"></div>
            </td>
          </tr>
          <tr>
            <td
              :colspan="`${isMobile?6:11}`"
              :class="{'v-data-table__mobile-row':isMobile}"
              style="padding: 10px ; padding-bottom: 20px"
            >
              <div>
                {{ $t('address') }} : {{ (translator ? translator.address : '') }}
              </div>
              <div>
                {{ $t('phone') }} : {{ (translator ? translator.phone : '') }}
              </div>
              <div>
                {{ $t('mobile') }} : {{ (translator ? translator.mobile : '') }}
              </div>
              <div>
                {{ (translator ? translator.content : '') }}
              </div>
            </td>
          </tr>
          <tr></tr>
        </template>
      </v-data-table>
    </div>
      <v-dialog
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      v-model="customSizeDialog"
      width="60%"
    >
      <v-card>
        <v-col
          class="d-flex pb-0"
        >
          <v-btn
            icon
            color="grey darken-2"
            @click="customSizeDialog = false"
          >
            <v-icon
              class="close-hover"
              large
            >
              mdi-close
            </v-icon>
          </v-btn>
          <p class="mx-auto font-weight-medium mt-2">
            {{ $t('print-size') }}
          </p>
        </v-col>
        <v-row class="ma-0">
          <v-col
            cols="12"
            sm="6"
            md="6"
          >
            <v-text-field
              v-model="width"
              outlined
              dense
              type="number"
              :label="$t('width') + '(cm)'"
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="6"
          >
            <v-text-field
              v-model="height"
              outlined
              dense
              type="number"
              :label="$t('height') + '(cm)'"
            />
          </v-col>
        </v-row>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col cols="6">
              <v-btn
                color="primary"
                @click="setCustomeSize"
              >
                {{ $t('submit') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="grey"
                outlined
                class="float-left"
                @click="customSizeDialog = false"
              >
                {{ $t('cancel') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snakbar"
      :color="(snakbarColor ? snakbarColor : '')"
      top
      style="z-index: 1000000000000 !important;"
    >
      {{ snakbarText }}

      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snakbar = !snakbar"
        >
          {{ $t('close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
  import store from '../../../../store'
  import General from '../../../../Mixins/general'
  export default {
    components: {
    },
    mixins: [General],
    props: ['headerProp', 'itemsProp', 'title', 'idProp'],
    data () {
      return {
        headerPrint: [],
        items: [],
        printType: 'unofficial',
        type: null,
        snakbarColor: null,
        snakbarText: null,
        snakbar: null,
        translator: null,
        content: null,
        id: null,
        date: null,
        account: null,
        number: null,
        currency_amount: null,
        dueDate: null,
        currency: null,
        tax: null,
        discount: null,
        entrepot: null,
        totalTax: null,
        totalDiscount: null,
        totalCount: null,
        totalFee: null,
        sum: null,
        width: null,
        height: null,
        user: null,
        customer: null,
        currencyName: '',
        self: null,
        headers: null,
        totalPrice: null,
        totalPayed: null,
        customSizeDialog: null,
        render: false,
        printSize: '29.7cm 21cm',
      }
    },
    computed: {
      isMobile () {
        return this.$vuetify.breakpoint.name === 'xs'
      },
      totals: function () {
        const totals = this.items.reduce((acc, d) => {
          acc.unofficial = (d.unofficial === 'active' ? 'active' : acc.unofficial)
          acc.stampTranslator = (d.stampTranslator === 'active' ? 'active' : acc.stampTranslator)
          acc.judicialApproval = (d.judicialApproval === 'active' ? 'active' : acc.judicialApproval)
          acc.foreignApproval = (d.foreignApproval === 'active' ? 'active' : acc.foreignApproval)
          acc.embassy = (d.embassy === 'active' ? 'active' : acc.embassy)
          return acc
        }, {
          unofficial: null,
          stampTranslator: null,
          judicialApproval: null,
          foreignApproval: null,
          embassy: null,
        })
        return totals
      },
    },
    watch: {
      printType () {
        // if (this.idProp && this.$refs.table) {
        var header = this.$refs.table.$el.querySelector('.v-data-table-header')
        header.deleteRow(0)
        header.deleteRow(0)
        this.setheader()
        //   this.show()
        // }
      },
      headerProp () {
        // this.head = this.itemsProp
      },
      itemsProp () {
        this.items = this.itemsProp
      },
    },
    beforeMount () {
      this.tableHeader()
      var storage = JSON.parse(localStorage.getItem('token'))
      if (storage != null) {
        store.commit('SET_START_DATE', storage.init.startDate)
        store.commit('SET_END_DATE', storage.init.endDate)
        store.commit('SET_DATE_LOCALE', storage.init.date_locale.value)
        if (storage.init.date_locale.value === 'fa,en') {
          store.commit('SET_DATE_FORMAT', 'jYYYY/jMM/jDD')
        } else {
          store.commit('SET_DATE_FORMAT', 'YYYY/MM/DD')
          store.commit('SET_START_DATE', null)
          store.commit('SET_END_DATE', null)
        }
        store.commit('SET_RTL', storage.init.rtl.value)
        store.commit('SET_DARK', storage.init.dark.value)
        store.commit('SET_LANG', storage.init.lang.value)
        store.commit('SET_THEME_COLOR', storage.init.theme_color.value)
      }
      this.$vuetify.theme.dark = this.$store.state.dark
      this.$root.$i18n.locale = this.$store.state.lang
      this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light'].primary = this.$store.state.theme_color
      if (this.$store.state.rtl) {
        this.$vuetify.rtl = true
        storage.init.rtl.value = true
        localStorage.setItem('token', JSON.stringify(storage))
      } else {
        this.$vuetify.rtl = false
      }
      if (this.$route.params.id) {
        this.id = this.$route.params.id
        this.show()
      }
      if (this.idProp) {
        this.id = this.idProp
        this.show()
      }
    },
    mounted () {
    },
    methods: {
      edit () {
        this.$router.push({ name: this.type + 'Edit', params: { id: this.idProp } })
      },
      entrepotPrint () {
        this.$router.push({ name: this.entrepot.type + 'Print', params: { id: this.entrepot.id } })
      },
      tableHeader () {
        this.headers = [
          {
            text: this.$t('row'),
            align: 'center',
            sortable: false,
            value: 'row',
            width: 20,
          },
          {
            text: this.$t('documentType'),
            sortable: false,
            value: 'documentType.name',
            align: 'center',
          },
          {
            text: this.$t('baseCount'),
            sortable: false,
            value: 'baseCount',
            align: 'center',
          },
          {
            text: this.$t('addDocumentCount'),
            sortable: false,
            value: 'addDocumentCount',
            align: 'center',
          },
          {
            text: this.$t('fromLanguage'),
            sortable: false,
            value: 'fromLanguage.name',
            align: 'center',
          },
          {
            text: this.$t('toLanguage'),
            sortable: false,
            value: 'toLanguage.name',
            align: 'center',
          },
          {
            text: this.$t('stampTranslator'),
            sortable: false,
            value: 'stampTranslator',
            align: 'center',
          },
          {
            text: this.$t('judicialApproval'),
            sortable: false,
            value: 'judicialApproval',
            align: 'center',
          },
          {
            text: this.$t('foreignApproval'),
            sortable: false,
            value: 'foreignApproval',
            align: 'center',
          },
          {
            text: this.$t('translationTariff'),
            sortable: false,
            value: 'basePrice',
            align: 'center',
          },
          {
            text: this.$t('translationCost&approval'),
            sortable: false,
            value: 'totalCost',
            align: 'center',
          },
        ]
      },
      setheader () {
        var elm = this.$refs.table.$el.querySelector('.v-data-table-header')
        var tr1 = document.createElement('tr')
        var tr2 = document.createElement('tr')
        // tr1.innerHTML = '' +
        //   '<th colspan="3">' +
        //   '<div align="right"><img class="mx-1" src="' + this.self.logo.link + '" width="50"></div>' +
        //   '</th>' +
        //   '<th colspan="3">' +
        //   '<div align="center" style="font-size: 16px">' + this.$t('print') + ' ' + this.$t(this.type) + ' </div>' +
        //   '<div align="center"> ' + this.self.abrName + '</div>' +
        //   '</th>' +
        //   '<th colspan="2">' +
        //   '<div align="left">' + this.$t('date') + ': ' + this.date + ' </div>' +
        //   '<div align="left">' + this.$t('number') + ': ' + this.number + ' </div>' +
        //   '<div align="left">' + this.$t('registerar') + ': ' + this.user.name + ' </div>' +
        //   '</th>'
        tr1.innerHTML = '' +
          '<th colspan="11">' +
          '<div style="display: flex;padding: 10px">' +
          '<div style="width: 33.33%">' +
          '<div align="right"><img class="mx-1" src="' + (this.self.logo ? this.self.logo.link : '') + '" width="50"></div>' +
          '</div>' +
          '<div style="width: 33.33% ">' +
          '<div align="center" style="font-size: 20px">' + (this.translator && this.translator.business_name ? this.translator.business_name : this.self.abrName) + ' </div>' +
          '<div align="center" style="font-size: 16px">' + this.$t('translationOrder') + ' </div>' +
          '<div align="center"> ' + this.self.invoiceSubtitle + '</div>' +
          '</div>' +
          '<div style="width: 33.33%">' +
          '<div align="left">' + this.$t('date') + ': ' + this.date + ' </div>' +
          '<div align="left">' + this.$t('deliveryDate') + ': ' + this.dueDate + ' </div>' +
          '<div align="left">' + this.$t('invoic-number') + ': ' + this.number + ' </div>' +
          '<div align="left">' + this.$t('registerar') + ': ' + this.user.name + ' </div>' +
          '</div>' +
          '</div>' +
          '</th>'
        if (this.printType === 'official') {
          tr2.innerHTML = '' +
            '<th colspan="6" style="padding: 6px 6px">' +
            '<div align="right">' + this.$t('seller') + ': ' + this.self.abrName + ' </div>' +
            '<div align="right">' + this.$t('phone') + ': ' + (this.self.phone ? this.self.phone : '') + ' </div>' +
            '<div align="right">' + this.$t('mobile') + ': ' + (this.self.mobile ? this.self.mobile : '') + ' </div>' +
            '<div align="right">' + this.$t('address') + ': ' + (this.self.address ? this.self.address : '') + ' </div>' +
            '</th>' +
            '<th colspan="5" style="padding: 6px 6px">' +
            '<div align="right">' + this.$t('customer') + ': ' + this.customer.name + ' </div>' +
            '<div align="right">' + this.$t('phone') + ': ' + (this.customer.phone ? this.customer.phone : '') + ' </div>' +
            '<div align="right">' + this.$t('mobile') + ': ' + (this.customer.mobile ? this.customer.mobile : '') + ' </div>' +
            '<div align="right">' + this.$t('address') + ': ' + (this.customer.address ? this.customer.address : '') + ' </div>' +
            '</th>'
        } else {
          tr2.innerHTML = '' +
            '<th colspan="11" style="padding: 6px 6px">' +
            '<div align="right">' + this.$t('customer') + ': ' + this.customer.name + ' </div>' +
            '<div align="right">' + this.$t('phone') + ': ' + (this.customer.phone ? this.customer.phone : '') + ' </div>' +
            '<div align="right">' + this.$t('mobile') + ': ' + (this.customer.mobile ? this.customer.mobile : '') + ' </div>' +
            '<div align="right">' + this.$t('address') + ': ' + (this.customer.address ? this.customer.address : '') + ' </div>' +
            '</th>'
        }

        elm.prepend(tr2)
        elm.prepend(tr1)
      },
      show () {
        var app = this
        app.loading = true
        app.$axios.get(this.$endpoint + 'translation/' + this.id + '/edit', {
          params: {
            id: this.idProp,
            print: 'active',
          },
        })
          .then(res => {
            app.$nextTick(() => {
              res = res.data
              app.id = res.id
              app.currency = res.currency
              app.currency_amount = res.currency_amount
              app.currencyName = res.currency.name
              app.tableHeader()
              app.number = res.number
              app.content = res.content
              app.date = res.date
              app.dueDate = res.delivery_date
              app.type = res.type
              app.items = res.article
              app.account = res.customer
              app.translator = res.translator
              app.entrepot = res.entrepot
              app.payments = res.payments
              app.checks = res.checks
              app.status = (res.last_status ? res.last_status.status : null)
              app.discount = res.discount
              app.totalDiscount = res.discount
              app.totalPrice = res.price
              app.totalPayed = res.payed
              app.user = res.user
              app.customer = res.customer
              app.self = res.self
              app.render = true
              setTimeout(function () {

              }, 1000)
            })
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => {
            app.setheader()
            app.loading = false
          })
      },
      clearCustom () {
        this.height = null
        this.width = null
      },
      setCustomeSize () {
        this.printSize = this.width + 'cm ' + this.height + 'cm'
        this.customSizeDialog = false
      },
      print () {
        var direction = 'ltr'
        if (this.$vuetify.rtl) {
          direction = 'rtl'
        }

        var style = '<style>' +
          'table { border: none; border-collapse: collapse; }' +
          'table td { border-left: 1px solid #c3c3c3; }' +
          'table th { border-left: 1px solid #c3c3c3; }' +
          'table td:first-child { border-right: 1px solid #c3c3c3;  }' +
          'table th:first-child { border-right: 1px solid #c3c3c3;  }' +
          'tbody > tr:last-child > td {border: 0px;}' +
          'thead > tr:first-child > th { border-top: 1px solid #c3c3c3; }' +
          '@bottom-left {content: normal;margin: 3px;}' +
          '</style>'
        setTimeout(function () {
          // Get HTML to print from element
          const prtHtml = document.getElementById('print').innerHTML

          let stylesHtml = ''
          let node = null
          for (node of [...document.querySelectorAll('link[rel="stylesheet"], style')]) {
            stylesHtml += node.outerHTML
          }

          const WinPrint = window.open('', '', 'left=0,top=0,width=1100,height=900,toolbar=0,scrollbars=0,status=0')
          WinPrint.document.write('<!DOCTYPE html> <html> <head> ' + stylesHtml + ' ' + style + ' </head> <body style="direction: ' + direction + '">' + prtHtml + ' </body> </html>')
          WinPrint.document.close()
          WinPrint.focus()
          setTimeout(function () {
            WinPrint.print()
            WinPrint.close()
          }, 2000)
        }, 50)
      },
      copied () {
        this.snakbarText = this.$t('copied')
        this.snakbarColor = 'blue'
        this.snakbar = true
      },
    },
  }
</script>

<style scoped>
.share-button {
  width: 28px;
  height: 28px;
  min-width: 0px !important;
  min-height: 0px !important;
  padding: 0px 0px !important;
}
.v-list-item__action {
  margin: 5px 0 !important;
}
.v-list-item__content{
  padding: 2px 0px !important;
}
.v-list-item--dense, .v-list--dense .v-list-item {
  min-height: 23px !important;
}
</style>
